import { useCallback } from 'react';

/**
 * Haptic Feedback Hook per iOS/Android native feel
 * Utilizza l'API Vibration per feedback tattile
 */
export function useHaptics() {
  // Tipi di feedback supportati
  type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';
  
  const triggerHaptic = useCallback((type: HapticType = 'light') => {
    // Verifica supporto
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;
    
    // Pattern di vibrazione per tipo
    const patterns: Record<HapticType, number | number[]> = {
      light: 10,
      medium: 20,
      heavy: 40,
      success: [10, 50, 10, 50],
      warning: [20, 100, 20],
      error: [30, 50, 30, 50, 30, 100],
      selection: 5,
    };
    
    try {
      navigator.vibrate(patterns[type]);
    } catch (e) {
      // Silently fail if vibration not supported
    }
  }, []);

  // Selection feedback (per UI changes)
  const selectionFeedback = useCallback(() => {
    triggerHaptic('selection');
  }, [triggerHaptic]);

  // Impact feedback
  const impactLight = useCallback(() => {
    triggerHaptic('light');
  }, [triggerHaptic]);

  const impactMedium = useCallback(() => {
    triggerHaptic('medium');
  }, [triggerHaptic]);

  const impactHeavy = useCallback(() => {
    triggerHaptic('heavy');
  }, [triggerHaptic]);

  // Notification feedback
  const notificationSuccess = useCallback(() => {
    triggerHaptic('success');
  }, [triggerHaptic]);

  const notificationWarning = useCallback(() => {
    triggerHaptic('warning');
  }, [triggerHaptic]);

  const notificationError = useCallback(() => {
    triggerHaptic('error');
  }, [triggerHaptic]);

  return {
    triggerHaptic,
    selectionFeedback,
    impactLight,
    impactMedium,
    impactHeavy,
    notificationSuccess,
    notificationWarning,
    notificationError,
  };
}

export default useHaptics;
